import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  CircularProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from "@material-ui/icons";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  progressBar: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    height: 10,
    borderRadius: 5
  },
  percentText: {
    fontWeight: 700,
    fontSize: "1.75rem",
    color: theme.palette.primary.main,
    textAlign: "center",
    marginTop: theme.spacing(1)
  },
  messageText: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    minHeight: 48
  },
  iconRow: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(1)
  }
}));

const StatusSendProgressModal = ({ open, onClose, activeUserId }) => {
  const classes = useStyles();
  const { socket, user } = useContext(AuthContext);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("running");
  const lastProgressRef = useRef(0);
  const creepTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setProgress(0);
      setMessage("");
      setStatus("running");
      lastProgressRef.current = 0;
      if (creepTimerRef.current) {
        clearInterval(creepTimerRef.current);
        creepTimerRef.current = null;
      }
      return;
    }

    setProgress(2);
    setMessage(i18n.t("whatsappStatus.progress.starting"));
    setStatus("running");
    lastProgressRef.current = 2;

    creepTimerRef.current = setInterval(() => {
      if (lastProgressRef.current >= 92) return;
      const next = Math.min(92, lastProgressRef.current + 1);
      lastProgressRef.current = next;
      setProgress(next);
    }, 2000);

    return () => {
      if (creepTimerRef.current) {
        clearInterval(creepTimerRef.current);
        creepTimerRef.current = null;
      }
    };
  }, [open]);

  useEffect(() => {
    const companyId = user?.companyId;
    if (!open || !socket || !companyId) return;

    const eventName = `company-${companyId}-whatsappStatusProgress`;

    const onProgress = (data) => {
      const targetUserId = activeUserId || user?.id;
      if (!data || Number(data.userId) !== Number(targetUserId)) return;

      if (data.progress !== undefined) {
        const next = Math.max(lastProgressRef.current, Number(data.progress) || 0);
        lastProgressRef.current = next;
        setProgress(next);
      }

      if (data.message) {
        setMessage(data.message);
      }

      if (data.phase === "completed") {
        setStatus("completed");
        setProgress(100);
        lastProgressRef.current = 100;
        if (creepTimerRef.current) {
          clearInterval(creepTimerRef.current);
          creepTimerRef.current = null;
        }
        setTimeout(() => onClose(), 1800);
      } else if (data.phase === "error") {
        setStatus("error");
        setProgress(100);
        if (creepTimerRef.current) {
          clearInterval(creepTimerRef.current);
          creepTimerRef.current = null;
        }
      }
    };

    socket.on(eventName, onProgress);

    return () => {
      socket.off(eventName, onProgress);
    };
  }, [open, socket, user?.companyId, user?.id, activeUserId, onClose]);

  const handleClose = () => {
    if (status === "running") return;
    onClose();
  };

  const getTitle = () => {
    if (status === "completed") {
      return i18n.t("whatsappStatus.progress.completedTitle");
    }
    if (status === "error") {
      return i18n.t("whatsappStatus.progress.errorTitle");
    }
    return i18n.t("whatsappStatus.progress.title");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      disableBackdropClick={status === "running"}
      disableEscapeKeyDown={status === "running"}
    >
      <DialogTitle style={{ textAlign: "center" }}>{getTitle()}</DialogTitle>
      <DialogContent>
        <Box className={classes.iconRow}>
          {status === "running" && <CircularProgress size={36} />}
          {status === "completed" && (
            <CheckCircleIcon style={{ color: "#4caf50", fontSize: 40 }} />
          )}
          {status === "error" && (
            <ErrorIcon style={{ color: "#f44336", fontSize: 40 }} />
          )}
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          className={classes.progressBar}
          color={status === "error" ? "secondary" : "primary"}
        />

        <Typography className={classes.percentText}>{progress}%</Typography>

        <Typography variant="body2" className={classes.messageText}>
          {message || i18n.t("whatsappStatus.progress.wait")}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="primary"
          variant="contained"
          fullWidth
          disabled={status === "running"}
        >
          {status === "running"
            ? i18n.t("whatsappStatus.progress.waitButton")
            : i18n.t("whatsappStatus.progress.closeButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusSendProgressModal;
